import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { FilterGroup } from '../../core/types/workspaceTypes';
import { buildFilterWhereClause } from './filterWhereBuilder';
import { quoteIdentifier } from './whereClauseBuilder';

export const JSON_SERVER_SCAN_LIMIT = 1000;

/**
 * Build a `SELECT col FROM schema.table WHERE col IS NOT NULL [AND guard]
 * [ORDER BY pk] LIMIT N` query for sampling JSON values from the server.
 *
 * The validity guard is added for declared-on-text columns on engines that
 * support it (MSSQL `ISJSON`, Oracle `IS JSON`, SQLite `json_valid`); native
 * JSON columns (PG `jsonb`, MySQL `json`, etc.) skip the guard.
 *
 * `pkColumns` (when non-empty) drives a deterministic `ORDER BY` over all
 * supplied columns so that repeated scans surface the same rows — important
 * for the cached prevalence numbers to be stable across refreshes
 * (especially on PG/MySQL where without ORDER BY the engine is free to
 * return rows in any order). Composite PKs are supported by joining every
 * column into the ORDER BY clause. When omitted (or empty) the SQL has no
 * ORDER BY and rows can shift between scans; callers that lack PK info
 * accept that tradeoff.
 *
 * Engine-specific limit syntax:
 *   - MSSQL / mock → `SELECT TOP (N) ...`
 *   - Oracle → `... FETCH FIRST N ROWS ONLY`
 *   - PG / MySQL / SQLite → `... LIMIT N`
 */
export function buildJsonScanSql(params: {
  engine: DatabaseEngineId;
  schema: string;
  table: string;
  column: string;
  declaredOnText: boolean;
  /**
   * Primary-key columns used for `ORDER BY` to make the scan reproducible.
   * Supports composite PKs — every column is included in the ORDER BY in
   * the supplied order. If empty/undefined (no PK / metadata not loaded),
   * ORDER BY is omitted and repeated scans may return different row sets.
   */
  pkColumns?: string[];
  limit?: number;
  /**
   * Active Table Explorer filters to scope the scan to (filter-aware peek).
   * Emitted via the renderer-side buildFilterWhereClause — kept emit-
   * equivalent with the main-process TE builder, EXCEPT the main-only
   * `lookup`→array_contains lowering on PG array columns (acceptable:
   * discriminator filters are scalar in practice).
   */
  scanFilters?: FilterGroup[];
  /**
   * The table's column → engine data type for `scanFilters` (the popover's
   * table metadata), so a date filter takes the engine's temporal literal
   * exactly as in Table Explorer. Omitted → values written as typed.
   */
  scanColumnTypeOf?: (columnName: string) => string | undefined;
}): string {
  const { engine, schema, table, column, declaredOnText, pkColumns } = params;
  const limit = params.limit ?? JSON_SERVER_SCAN_LIMIT;
  const qSchema = schema ? quoteIdentifier(schema, engine) : '';
  const qTable = quoteIdentifier(table, engine);
  const qCol = quoteIdentifier(column, engine);
  const tableRef = qSchema ? `${qSchema}.${qTable}` : qTable;
  const guard = declaredOnText ? jsonValidityGuard(engine, qCol) : '';
  const whereClauses = [`${qCol} IS NOT NULL`];
  if (guard) whereClauses.push(guard);
  const filterClause =
    params.scanFilters && params.scanFilters.length > 0
      ? buildFilterWhereClause(params.scanFilters, engine, params.scanColumnTypeOf)
      : '';
  if (filterClause) whereClauses.push(filterClause);
  const where = `WHERE ${whereClauses.join(' AND ')}`;
  const orderBy =
    pkColumns && pkColumns.length > 0
      ? ` ORDER BY ${pkColumns.map((c) => quoteIdentifier(c, engine)).join(', ')}`
      : '';

  if (engine === 'mssql' || engine === 'mock') {
    return `SELECT TOP (${limit}) ${qCol} FROM ${tableRef} ${where}${orderBy}`;
  }
  if (engine === 'oracle') {
    return `SELECT ${qCol} FROM ${tableRef} ${where}${orderBy} FETCH FIRST ${limit} ROWS ONLY`;
  }
  return `SELECT ${qCol} FROM ${tableRef} ${where}${orderBy} LIMIT ${limit}`;
}

function jsonValidityGuard(engine: DatabaseEngineId, qCol: string): string {
  switch (engine) {
    case 'mssql':
      return `ISJSON(${qCol}) = 1`;
    case 'oracle':
      return `${qCol} IS JSON`;
    case 'sqlite':
      return `json_valid(${qCol})`;
    case 'pgsql':
    case 'mysql':
    case 'mock':
    default:
      return '';
  }
}
